import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { IconButton } from "@/components/ui/IconButton";
import { cn } from "@/lib/cn";
import { useScrollLock } from "@/lib/scrollLock";
import { useT } from "@/lib/i18n";

export type ModalProps = {
  title: string;
  description?: string | undefined;
  onClose: () => void;

  footer?: ReactNode | undefined;

  subheader?: ReactNode | undefined;
  size?: "sm" | "md" | "lg" | undefined;

  onStage?: boolean | undefined;
  children: ReactNode;
};

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" } as const;

export function Modal({
  title,
  description,
  onClose,
  footer,
  subheader,
  size = "md",
  onStage = false,
  children,
}: ModalProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const t = useT();

  useScrollLock();

  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const element = dialog.current;
    if (element === null || element.open) return undefined;
    element.showModal();

    const onBackdrop = (event: MouseEvent) => {
      if (event.target === element) close.current();
    };
    element.addEventListener("click", onBackdrop);

    return () => {
      element.removeEventListener("click", onBackdrop);
      element.close();
    };
  }, []);

  return (
    <dialog
      className={cn(
        "m-auto w-full rounded-2xl p-0 shadow-overlay backdrop:bg-black/50",
        "max-h-[90dvh] max-sm:mb-0 max-sm:rounded-b-none",

        "flex flex-col overflow-clip",

        "cursor-auto backdrop:cursor-default",
        SIZES[size],
        onStage ? "bg-stage-raised text-stage-ink" : "bg-surface text-ink",
      )}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      ref={dialog}
    >
      <header
        className={cn(
          "flex shrink-0 justify-between gap-4 border-b px-5 py-4",

          description === undefined ? "items-center" : "items-start",
          onStage ? "border-stage-line" : "border-line",
        )}
      >
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {description !== undefined ? (
            <p className={cn("mt-0.5 text-sm", onStage ? "text-stage-muted" : "text-ink-muted")}>
              {description}
            </p>
          ) : null}
        </div>
        <IconButton
          className={cn("-me-2", description !== undefined && "-mt-1")}
          label={t("common.close")}
          onClick={onClose}
          variant={onStage ? "stage" : "default"}
        >
          <X className="h-4 w-4" />
        </IconButton>
      </header>

      {subheader === undefined ? null : (
        <div
          className={cn("shrink-0 border-b px-5", onStage ? "border-stage-line" : "border-line")}
        >
          {subheader}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

      {footer !== undefined ? (
        <footer
          className={cn(
            "flex shrink-0 items-center justify-end gap-2 border-t px-5 py-4",
            onStage ? "border-stage-line" : "border-line",
          )}
        >
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}
